import { Chip } from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import React, { useMemo } from "react";

export interface SupplierOption {
  label: string;
  value: string;
  isParent?: boolean;
  isChild?: boolean;
}

export interface PaymentStatusFilterState {
  suppliers: string[];
  invoiceNumber: string;
  paymentDateRange: string;
  method: string;
  paymentStatus: string;
}

interface PaymentStatusFilterToolbarProps {
  filters: PaymentStatusFilterState;
  supplierOptions?: SupplierOption[];
  hierarchyMap?: Record<string, string[]>;
  isLoadingSuppliers?: boolean;
  onFilterChange: <K extends keyof PaymentStatusFilterState>(
    key: K,
    value: PaymentStatusFilterState[K],
  ) => void;
  onApply: () => void;
  onReset: () => void;
}

export const PaymentStatusFilterToolbar: React.FC<
  PaymentStatusFilterToolbarProps
> = ({
  filters,
  supplierOptions = [],
  hierarchyMap = {},
  isLoadingSuppliers = false,
  onFilterChange,
  onApply,
  onReset,
}) => {
  const dateRangeOptions = [
    "Next day",
    "Next 7 days",
    "Next 14 days",
    "Next 31 days",
    "Next 90 days",
    "Today",
    "Yesterday",
    "Last 7 days",
    "Last 14 days",
    "Last 31 days",
    "Last 90 days",
    "Last 365 days",
  ];

  const methodOptions = [
    { label: "Any", value: "" },
    { label: "CTX (ACH)", value: "CTX" },
    { label: "SY (Check)", value: "SY" },
    { label: "EDI (ACH)", value: "EDI" },
  ];

  const paymentStatusOptions = [
    { label: "Any", value: "" },
    { label: "Pending", value: "Pending" },
    { label: "Paid", value: "Paid" },
    { label: "Void", value: "Void" },
  ];

  const validSuppliers = (filters.suppliers || []).filter(
    (s) => s && s.trim() !== "",
  );

  const effectiveSupplierCount = useMemo(() => {
    if (validSuppliers.length === 0) return 0;

    const effectiveList: string[] = [];

    validSuppliers.forEach((id) => {
      const subVendors = hierarchyMap[id];
      if (subVendors && subVendors.length > 0) {
        effectiveList.push(...subVendors);
      } else {
        effectiveList.push(id);
      }
    });

    return new Set(effectiveList).size;
  }, [validSuppliers, hierarchyMap]);

  const isSupplierValid =
    isLoadingSuppliers ||
    (effectiveSupplierCount >= 1 && effectiveSupplierCount <= 10);

  const isApplyDisabled = !isSupplierValid || isLoadingSuppliers;

  const isDateRangeDisabled = Boolean(filters.invoiceNumber.trim());

  const handlePaymentStatusSelect = (statusValue: string) => {
    onFilterChange("paymentStatus", statusValue);
    if (statusValue === "") {
      onFilterChange("paymentDateRange", "Next 90 days");
    }
  };

  const handleInvoiceNumberChange = (invoiceValue: string) => {
    onFilterChange("invoiceNumber", invoiceValue);
    if (invoiceValue === "") {
      onFilterChange("paymentDateRange", "Next 90 days");
    }
  };

  const getApplyButtonStyle = (disabled: boolean): React.CSSProperties => ({
    backgroundColor: disabled ? "#a5d6a7" : "#168a58",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    padding: "7px 20px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 2px 4px rgba(0, 0, 0, 0.15)",
  });

  const resetButtonStyle: React.CSSProperties = {
    backgroundColor: "#168a58",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    padding: "7px 20px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
  };

  return (
    <div style={{ marginBottom: "20px", fontFamily: "inherit" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 220px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "4px",
              color: isSupplierValid ? "inherit" : "#d32f2f",
            }}
          >
            Suppliers
          </label>
          <Autocomplete
            multiple
            disabled={isLoadingSuppliers}
            options={supplierOptions}
            getOptionLabel={(option) => option.label || ""}
            value={supplierOptions.filter((opt) =>
              validSuppliers.includes(opt.value),
            )}
            onChange={(_, newValue) => {
              onFilterChange(
                "suppliers",
                newValue.map((item) => item.value),
              );
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props as any;
              return (
                <Box
                  component="li"
                  key={key}
                  {...otherProps}
                  sx={{
                    fontWeight: option.isParent ? 700 : 400,
                    fontSize: "13px",
                    paddingLeft: option.isChild ? "20px !important" : "12px",
                  }}
                >
                  {option.label}
                </Box>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.value}
                    size="small"
                    {...tagProps}
                    style={{ height: "24px", fontSize: "12px", margin: "2px" }}
                  />
                );
              })
            }
            style={{ width: "100%", backgroundColor: "#ffffff" }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!isSupplierValid}
                helperText={
                  !isSupplierValid
                    ? effectiveSupplierCount < 1
                      ? "Select at least 1 supplier"
                      : "Maximum 10 suppliers including child vendors"
                    : ""
                }
                placeholder={
                  isLoadingSuppliers
                    ? "Loading suppliers..."
                    : validSuppliers.length === 0
                      ? "Select supplier(s)"
                      : ""
                }
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: "3px 8px",
                    fontSize: "13px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isSupplierValid ? "#ccc" : "#d32f2f",
                  },
                }}
              />
            )}
          />
        </div>

        {/* Payment Status Dropdown */}
        <div style={{ flex: "1 1 140px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            Payment Status
          </label>
          <select
            value={filters.paymentStatus}
            onChange={(e) => handlePaymentStatusSelect(e.target.value)}
            style={{
              width: "100%",
              padding: "8.5px 8px",
              fontSize: "13px",
              boxSizing: "border-box",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          >
            {paymentStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Invoice Number Input */}
        <div style={{ flex: "1 1 150px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            Invoice Number
          </label>
          <input
            type="text"
            placeholder="Type input"
            value={filters.invoiceNumber}
            onChange={(e) => handleInvoiceNumberChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8.5px 8px",
              fontSize: "13px",
              fontStyle: filters.invoiceNumber ? "normal" : "italic",
              boxSizing: "border-box",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        {/* Payment Date Range Dropdown */}
        <div style={{ flex: "1 1 170px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "4px",
              color: isDateRangeDisabled ? "#888" : "inherit",
            }}
          >
            Scheduled Payment Date
          </label>
          <select
            disabled={isDateRangeDisabled}
            value={isDateRangeDisabled ? "" : filters.paymentDateRange}
            onChange={(e) => onFilterChange("paymentDateRange", e.target.value)}
            style={{
              width: "100%",
              padding: "8.5px 8px",
              fontSize: "13px",
              boxSizing: "border-box",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: isDateRangeDisabled ? "#f5f5f5" : "#ffffff",
              cursor: isDateRangeDisabled ? "not-allowed" : "default",
            }}
          >
            {isDateRangeDisabled && <option value=""></option>}
            {dateRangeOptions.map((opt) => (
              <option key={opt} value={opt} defaultValue={"Next 90 days"}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Payment Method Dropdown */}
        <div style={{ flex: "1 1 130px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            Method
          </label>
          <select
            value={filters.method}
            onChange={(e) => onFilterChange("method", e.target.value)}
            style={{
              width: "100%",
              padding: "8.5px 8px",
              fontSize: "13px",
              fontStyle: filters.method === "" ? "italic" : "normal",
              boxSizing: "border-box",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          >
            {methodOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={onApply}
            disabled={isApplyDisabled}
            style={getApplyButtonStyle(isApplyDisabled)}
          >
            Apply
          </button>
          <button onClick={onReset} style={resetButtonStyle}>
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};
